import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import getBillingTypeHelperText from "./BillingTypeHelperText";
import { extractBillingTypes } from "../../../utils/billingHelpers";

const styles = {
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
  },
  listItem: {
    marginBottom: 0.5,
  },
  listItemButton: {
    py: 2,
    cursor: "grab",
    minHeight: 60,
  },
  listItemText: {
    flex: 1,
  },
};

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const getListStyle = isDraggingOver => ({
  background: isDraggingOver ? "#f5f5f5" : "white",
  borderRadius: "4px",
  padding: "8px",
});

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: "none",
  background: isDragging ? "#e3f2fd" : "white",
  border: isDragging ? "1px solid #1976d2" : "1px solid #e0e0e0",
  borderRadius: "4px",
  ...draggableStyle,
});

export default function BillingTypePriorityModal({ 
  open, 
  onClose, 
  selectedBillingTypes = [], 
  onSave,
  category = 'inbound'
}) {
  const [step, setStep] = useState(1);
  const [orderedBillingTypes, setOrderedBillingTypes] = useState([]);
  const [billingTypeComments, setBillingTypeComments] = useState({}); 
  
  useEffect(() => {
    if (open) {
      // Initialize ordered billing types and comments using helper
      const { types, comments } = extractBillingTypes(selectedBillingTypes);
      setOrderedBillingTypes(types || []);
      setBillingTypeComments(comments || {});
      setStep(1);
    }
  }, [open, selectedBillingTypes]);

  const handleDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const reorderedTypes = reorder(
      orderedBillingTypes,
      result.source.index,
      result.destination.index
    );
    setOrderedBillingTypes(reorderedTypes);
  };

  const handleNext = () => {
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleCommentChange = (billingType, value) => {
    setBillingTypeComments(prev => ({
      ...prev,
      [billingType]: value
    }));
  };

  const handleSave = () => {
    const dataToSave = {
      types: orderedBillingTypes,
      comments: billingTypeComments
    };
    onSave(dataToSave);
    onClose();
  };

  const handleClose = () => {
    setStep(1);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography fontSize={24} fontWeight={500} color="textPrimary" align="center">
          {step === 1 ? "Organize Billing Types" : "When to Use Each Billing Type"}
        </Typography>
        <IconButton onClick={handleClose} sx={styles.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box mt={2}>
          {step === 1 ? (
            // Step 1: Organize billing types
            <>
              {orderedBillingTypes.length === 0 ? (
                <Typography variant="body1" color="text.secondary" align="center" py={4}>
                  No billing types available
                </Typography>
              ) : (
                <>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 , textAlign: 'center' }}>
                    Drag to set the order. The first type will be shown as the most common choice for this customer, giving your warehouse team a clear starting point.
                  </Typography>
                  
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="billing-types-list">
                      {(provided, snapshot) => (
                        <List
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          style={getListStyle(snapshot.isDraggingOver)}
                          dense
                        >
                          {orderedBillingTypes.map((billingType, index) => (
                            <Draggable 
                              key={billingType} 
                              draggableId={billingType} 
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <ListItem
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  disablePadding
                                  style={getItemStyle(
                                    snapshot.isDragging, 
                                    provided.draggableProps.style
                                  )}
                                  sx={styles.listItem}
                                >
                                  <ListItemButton 
                                    sx={styles.listItemButton} 
                                    {...provided.dragHandleProps}
                                  >
                                    <ListItemText
                                      primary={
                                        <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                          {billingType}
                                        </Typography>
                                      }
                                      sx={styles.listItemText}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </List>
                      )}
                    </Droppable>
                  </DragDropContext>

                  <Box mt={3} display="flex" justifyContent="flex-end">
                    <Button 
                      onClick={handleNext}
                      variant="contained"
                      color="primary"
                    >
                      Next
                    </Button>
                  </Box>
                </>
              )}
            </>
          ) : (
            // Step 2: Add comments for each billing type
            <>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                Add clear guidelines for each billing type so your 
                <br />
                warehouse team can choose the right option for every transaction.
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {orderedBillingTypes.map((billingType, index) => (
                  <Paper key={billingType} sx={{ p: 2 }}>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body1" fontWeight={500}>
                        {billingType}
                        {index === 0 && orderedBillingTypes.length > 1 && (
                          <Typography component="span" color="primary.main">
                            {" (most common)"}
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      variant="outlined"
                      placeholder={`When should the warehouse select "${billingType}"?`}
                      value={billingTypeComments[billingType] || ''}
                      onChange={(e) => handleCommentChange(billingType, e.target.value)}
                      // helperText={getBillingTypeHelperText(billingType)} // Temporarily commented out
                      sx={{
                        '& .MuiInputBase-root': {
                          fontSize: '14px',
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: '12px',
                          color: 'text.secondary',
                        }
                      }}
                    />
                  </Paper>
                ))}
              </Box>

              <Box mt={3} display="flex" justifyContent="space-between">
                <Button 
                  onClick={handleBack}
                  variant="outlined"
                  color="primary"
                >
                  Back
                </Button>
                <Button 
                  onClick={handleSave}
                  variant="contained"
                  color="primary"
                >
                  Save Configuration
                </Button>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
